import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { FileSpreadsheet, FileText, FileDown, Loader2, CalendarRange, Info } from 'lucide-react';
import { toast } from 'sonner';
import FAQLink from './faqs/FAQLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatRange(fromIso, toIso) {
  if (!fromIso || !toIso) return '—';
  try {
    const fmt = (iso) => {
      const d = new Date(iso + 'T00:00:00Z');
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    };
    return `${fmt(fromIso)} → ${fmt(toIso)}`;
  } catch {
    return `${fromIso} → ${toIso}`;
  }
}

function TaxYearPicker({ presets, activePresetKey, handlePresetClick, days, months, startDay, startMonth, setStartDay, setStartMonth, prefsDirty, savingPrefs, handleSavePrefs }) {
  return (
    <div className="mt-2">
      <Label className="text-sm font-medium text-slate-700">Tax year</Label>
      <div className="mt-2 flex flex-wrap gap-2" data-testid="tax-year-presets">
        {presets.map((p) => {
          const isActive = activePresetKey === p.key;
          return (
            <button key={p.key} type="button" onClick={() => handlePresetClick(p)}
              className={`text-xs rounded-full border px-3 py-1.5 transition ${isActive ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-500 hover:text-emerald-800'}`}
              data-testid={`tax-year-preset-${p.key}`} title={p.jurisdictions}>{p.label}</button>
          );
        })}
        <span className={`text-xs rounded-full border px-3 py-1.5 ${activePresetKey === 'custom' ? 'bg-slate-700 text-white border-slate-700' : 'bg-slate-50 text-slate-500 border-slate-200'}`} data-testid="tax-year-preset-custom-indicator">Custom</span>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-slate-500">Starts</Label>
          <div className="mt-1 flex items-center gap-2">
            <select value={startDay} onChange={(e) => setStartDay(Number(e.target.value))} className="border border-slate-300 rounded-md text-sm px-2 py-1.5 bg-white" data-testid="tax-year-day-select">
              {days.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} className="border border-slate-300 rounded-md text-sm px-2 py-1.5 bg-white" data-testid="tax-year-month-select">
              {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <Button type="button" size="sm" disabled={!prefsDirty || savingPrefs} onClick={handleSavePrefs} className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50" data-testid="tax-year-save-btn">
          {savingPrefs ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving</>) : 'Save tax year'}
        </Button>
      </div>
    </div>
  );
}

function TaxDataPackPreviewAndDownloads({ previewLoading, preview, holdingsCount, eventCount, closedCount, totalCount, byType, formattedEarliestAfter, downloading, handleDownload, sharingEligible, onGoToSharing }) {
  return (
    <>
      <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm" data-testid="tax-data-pack-preview">
        {previewLoading ? (
          <div className="flex items-center text-slate-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Computing window…</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Last completed tax year</p>
                <p className="text-sm text-slate-900 font-medium" data-testid="tax-data-pack-window">{formatRange(preview?.from_date, preview?.to_date)}</p>
              </div>
              <Badge variant="outline" className={`text-xs ${totalCount > 0 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-600 border-slate-300'}`} data-testid="tax-data-pack-rows-count">
                {holdingsCount} holding{holdingsCount === 1 ? '' : 's'} · {eventCount} income event{eventCount === 1 ? '' : 's'} · {closedCount} closed
              </Badge>
            </div>
            {eventCount > 0 && (
              <div className="mt-2 text-xs text-slate-600 space-x-3">
                {Object.entries(byType).map(([t, agg]) => (
                  <span key={t}><span className="font-medium">{t.replace(/_/g, ' ')}</span>: {agg.count}</span>
                ))}
              </div>
            )}
            {totalCount === 0 && formattedEarliestAfter && (
              <p className="mt-2 text-xs text-slate-600" data-testid="tax-data-pack-future-hint">
                Your earliest derivable event falls in <span className="font-medium">{formattedEarliestAfter}</span>. Will be captured for the next tax year.
              </p>
            )}
          </>
        )}
      </div>
      <div className="mt-3 flex items-start gap-2 text-xs text-slate-600 rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="tax-data-pack-phase-note">
        <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <span><span className="font-semibold">What&apos;s inside:</span> holdings as of period end, income events (bond coupons, T-bill and FD interest, plus your recorded dividends &amp; distributions), and cycles completed within the period (maturities and discretionary sales with realised gain/loss).</span>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="tax-data-pack-downloads">
        <Button variant="outline" onClick={() => handleDownload('csv')} disabled={downloading !== null || previewLoading} className="justify-start border-slate-300 hover:border-emerald-500 hover:text-emerald-800" data-testid="tax-data-pack-download-csv">
          {downloading === 'csv' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2 text-emerald-700" />}Download CSV
        </Button>
        <Button variant="outline" onClick={() => handleDownload('xlsx')} disabled={downloading !== null || previewLoading} className="justify-start border-slate-300 hover:border-emerald-500 hover:text-emerald-800" data-testid="tax-data-pack-download-xlsx">
          {downloading === 'xlsx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-700" />}Download Excel
        </Button>
        <Button variant="outline" onClick={() => handleDownload('pdf')} disabled={downloading !== null || previewLoading} className="justify-start border-slate-300 hover:border-emerald-500 hover:text-emerald-800" data-testid="tax-data-pack-download-pdf">
          {downloading === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2 text-emerald-700" />}Download PDF
        </Button>
      </div>
      {sharingEligible && onGoToSharing && (
        <p className="mt-3 text-xs text-slate-500" data-testid="tax-data-pack-share-cross-ref">
          For live interactive read-only access to your portfolio, see{' '}
          <button type="button" onClick={onGoToSharing} className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2" data-testid="tax-data-pack-go-to-share-view">Share View</button>.
        </p>
      )}
    </>
  );
}

/**
 * Tax Data Pack settings card — Phase A (export-only).
 *
 * Subscriber picks a jurisdiction preset (or custom month/day), the card surfaces
 * a live preview (window + event count), then exports CSV/XLSX/PDF.
 */
export default function TaxDataPackSettings({ user, onUserUpdate, sharingEligible, onGoToSharing }) {
  const [presets, setPresets] = useState([]);
  const [startMonth, setStartMonth] = useState(user?.tax_year_start_month ?? 1);
  const [startDay, setStartDay] = useState(user?.tax_year_start_day ?? 1);
  // savedMonth/Day track what the SERVER currently has. Updated locally on a
  // successful PUT so we don't depend on the parent re-rendering with fresh user props.
  const [savedMonth, setSavedMonth] = useState(user?.tax_year_start_month ?? 1);
  const [savedDay, setSavedDay] = useState(user?.tax_year_start_day ?? 1);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'csv' | 'xlsx' | 'pdf' | null

  // Load presets once
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/tax-data-pack/presets`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { presets: [] }))
      .then((d) => setPresets(d.presets || []))
      .catch(() => setPresets([]));
  }, []);

  // Reload preview whenever the SERVER-CONFIRMED tax-year prefs change OR on first mount.
  // savedMonth/Day are updated locally on successful PUT, so we don't rely on the
  // parent component re-rendering with refreshed user props.
  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    fetch(`${BACKEND_URL}/api/tax-data-pack/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({}),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setPreview(d); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [savedMonth, savedDay]);

  const activePresetKey = presets.find(
    (p) => p.start_month === startMonth && p.start_day === startDay,
  )?.key || 'custom';

  const handlePresetClick = (p) => {
    setStartMonth(p.start_month);
    setStartDay(p.start_day);
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          tax_year_start_month: Number(startMonth),
          tax_year_start_day: Number(startDay),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save tax-year preference');
      }
      toast.success('Tax year saved');
      // Update local "saved" tracker — this triggers the preview useEffect and
      // keeps the component self-consistent even if the parent does not honour
      // onUserUpdate.
      setSavedMonth(Number(startMonth));
      setSavedDay(Number(startDay));
      onUserUpdate && onUserUpdate({
        ...user,
        tax_year_start_month: Number(startMonth),
        tax_year_start_day: Number(startDay),
      });
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleDownload = async (fmt) => {
    setDownloading(fmt);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tax-data-pack/export?fmt=${fmt}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${resp.status})`);
      }
      const blob = await resp.blob();
      // Pull filename from Content-Disposition
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const fallback = `myinvestfolio-tax-data-pack.${fmt}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: MONTH_LABELS[i] }));

  const holdingsCount = preview?.summary?.holdings_count ?? 0;
  const closedCount = preview?.summary?.closed_position_count ?? 0;
  const eventCount = preview?.summary?.income_event_count ?? preview?.summary?.event_count ?? 0;
  const totalCount = holdingsCount + closedCount + eventCount;
  const byType = preview?.summary?.by_event_type || {};
  const earliestAfter = preview?.earliest_event_after_window;
  const formattedEarliestAfter = (() => {
    if (!earliestAfter?.event_date) return null;
    try {
      const d = new Date(earliestAfter.event_date + 'T00:00:00Z');
      return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    } catch { return null; }
  })();
  const prefsDirty = Number(startMonth) !== Number(savedMonth)
    || Number(startDay) !== Number(savedDay);

  return (
    <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6" data-testid="tax-data-pack-section">
      <div className="flex items-start gap-2 mb-3">
        <CalendarRange className="w-5 h-5 text-emerald-700 mt-1 shrink-0" />
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-slate-900">Tax Data Pack</h3>
          <p className="body-text mt-1">
            A clean, country-agnostic record of the taxable inputs in your portfolio — sized to whatever
            tax year you (or your accountant) work in. <span className="italic">Reconciliation aid only,
            not a tax computation.</span>
          </p>
        </div>
      </div>

      {/* Tax-year picker */}
      <TaxYearPicker
        presets={presets} activePresetKey={activePresetKey} handlePresetClick={handlePresetClick}
        days={days} months={months} startDay={startDay} startMonth={startMonth}
        setStartDay={setStartDay} setStartMonth={setStartMonth}
        prefsDirty={prefsDirty} savingPrefs={savingPrefs} handleSavePrefs={handleSavePrefs}
      />

      {/* Preview panel + scope note + downloads + share cross-ref */}
      <TaxDataPackPreviewAndDownloads
        previewLoading={previewLoading} preview={preview}
        holdingsCount={holdingsCount} eventCount={eventCount} closedCount={closedCount}
        totalCount={totalCount} byType={byType} formattedEarliestAfter={formattedEarliestAfter}
        downloading={downloading} handleDownload={handleDownload}
        sharingEligible={sharingEligible} onGoToSharing={onGoToSharing}
      />
      <div className="mt-3">
        <FAQLink categoryKey="tax" />
      </div>
    </div>
  );
}
